import { MOVIE_SORTS, type MovieSort } from '@zyx/shared';
import { PageHeader } from '@/components/common/page-header';
import { EditionFilter, FILTER_TRIGGER, parseEdition } from '@/components/movie/edition-filter';
import { MovieGrid } from '@/components/movie/movie-grid';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useFilterParams } from '@/hooks/use-filter-params';
import { useTerms } from '@/hooks/use-movies';

const SORT_LABELS: Readonly<Record<MovieSort, string>> = {
  relevance: 'Best match',
  published: 'Newest',
  release: 'Release date',
  added: 'Recently indexed',
  views: 'Most viewed',
  watchlist: 'Watchlist order',
  list: 'List order',
};

const ANY = 'any';

/** Browsing and search results: the filters live in the URL, so a result is a link. */
export function BrowsePage() {
  const { params, set } = useFilterParams();
  const q = params.get('q') ?? undefined;
  const edition = parseEdition(params.get('edition'));
  const sort = MOVIE_SORTS.find((candidate) => candidate === params.get('sort')) ?? (q === undefined ? 'published' : 'relevance');
  const termId = Number(params.get('genre') ?? '') || undefined;
  // Genres the library holds, most movies first: the filter offers nothing it lacks.
  const genres = useTerms({ kind: 'genre', limit: 200 });
  const sorts = MOVIE_SORTS.filter((candidate) => candidate !== 'watchlist' && candidate !== 'list' && (q !== undefined || candidate !== 'relevance'));

  return (
    <div className="space-y-6">
      <PageHeader
        title={q === undefined ? 'Browse' : `Results for “${q}”`}
        description={q === undefined ? undefined : 'Codes match first; then titles, cast and genres, typos forgiven.'}
        actionsClassName="w-full sm:w-auto"
        actions={
          <>
            <Select value={termId === undefined ? ANY : String(termId)} onValueChange={(value) => set('genre', value === ANY ? undefined : value)}>
              <SelectTrigger className={FILTER_TRIGGER} aria-label="Genre">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>All genres</SelectItem>
                {termId === undefined || genres.data?.terms.some((genre) => genre.termId === termId) === true ? null : (
                  <SelectItem value={String(termId)}>{genres.data === undefined ? 'Loading…' : 'This genre'}</SelectItem>
                )}
                {(genres.data?.terms ?? []).map((genre) => (
                  <SelectItem key={genre.termId} value={String(genre.termId)}>
                    {genre.name} ({genre.movieCount.toLocaleString()})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <EditionFilter value={edition} onChange={(value) => set('edition', value)} />
            <Select value={sort} onValueChange={(value) => set('sort', value)}>
              <SelectTrigger className={FILTER_TRIGGER} aria-label="Sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sorts.map((candidate) => (
                  <SelectItem key={candidate} value={candidate}>
                    {SORT_LABELS[candidate]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
      />
      <MovieGrid
        request={{ q, edition, sort, termId }}
        empty={
          q === undefined
            ? { title: 'Nothing matches these filters' }
            : { title: `Nothing found for “${q}”`, description: 'Try fewer words, a code such as DSOD-052, or a name. Only indexed movies can be found.' }
        }
      />
    </div>
  );
}
